import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, UserPlus, Clock, TrendingUp, BarChart3 } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function EnhancedDashboard({ user, onLogout }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('week');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchAnalytics();
  }, [filter]);

  const fetchAnalytics = async () => {
    try {
      let url = `${API}/analytics/dashboard?filter_type=${filter}`;
      if (filter === 'custom' && customDates.start && customDates.end) {
        url += `&start_date=${customDates.start}&end_date=${customDates.end}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="dashboard"><div className="p-8">Loading...</div></Layout>;
  }

  const maxActivity = Math.max(...(analytics?.day_wise_activity.map(d => d.active_users) || [1]));

  return (
    <Layout user={user} onLogout={onLogout} currentPage="dashboard">
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy mb-2">
              Welcome, {user.first_name}!
            </h1>
            <p className="text-[#4D4D4D] font-light">Admin Dashboard & Analytics</p>
          </div>
        </div>

        {/* Filter Bar */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <label className="font-semibold text-navy">Time Period:</label>
              <div className="flex space-x-2">
                {['day', 'week', 'month'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? 'default' : 'outline'}
                    onClick={() => setFilter(f)}
                    className={filter === f ? 'bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl' : 'border-2 border-[#E7E7E7] rounded-xl hover:border-primary-blue'}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant={filter === 'custom' ? 'default' : 'outline'}
                  onClick={() => setFilter('custom')}
                  className={filter === 'custom' ? 'bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl' : 'border-2 border-[#E7E7E7] rounded-xl hover:border-primary-blue'}
                >
                  Custom
                </Button>
              </div>
              {filter === 'custom' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={customDates.start}
                    onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
                    className="px-3 py-1 border-2 border-[#E7E7E7] rounded-xl text-sm focus:border-primary-blue"
                  />
                  <span className="text-[#4D4D4D]">to</span>
                  <input
                    type="date"
                    value={customDates.end}
                    onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
                    className="px-3 py-1 border-2 border-[#E7E7E7] rounded-xl text-sm focus:border-primary-blue"
                  />
                  <Button size="sm" onClick={fetchAnalytics} className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl">
                    Apply
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Students */}
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl premium-gradient flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide mb-1">Total Students</p>
              <p className="text-3xl font-bold text-navy">{analytics?.total_students || 0}</p>
            </CardContent>
          </Card>

          {/* New Students */}
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl success-gradient flex items-center justify-center mb-3">
                <UserPlus className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide mb-1">New Students</p>
              <p className="text-3xl font-bold text-navy">{analytics?.new_students || 0}</p>
            </CardContent>
          </Card>

          {/* Active Students */}
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl streak-gradient flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide mb-1">Active Students</p>
              <p className="text-3xl font-bold text-navy">{analytics?.active_students || 0}</p>
            </CardContent>
          </Card>

          {/* Total Time Spent */}
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl rank-gradient flex items-center justify-center mb-3">
                <Clock className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide mb-1">Total Time Spent</p>
              <p className="text-3xl font-bold text-navy">{Math.round(analytics?.total_time_spent_hours || 0)}<span className="text-lg font-normal">h</span></p>
            </CardContent>
          </Card>
        </div>

        {/* Day-wise Activity Bar Chart */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <BarChart3 className="w-6 h-6 text-primary-blue" strokeWidth={2} />
              <h3 className="text-xl font-bold text-navy">Day-wise Activity</h3>
            </div>
            <div className="space-y-4">
              {analytics?.day_wise_activity.map((day, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-navy">{day.day_name}, {new Date(day.date).getDate()}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-[#4D4D4D]">{day.active_users} users</span>
                      <span className="text-sm font-semibold text-primary-blue">
                        {Math.round(day.estimated_time_minutes / 60)}h {day.estimated_time_minutes % 60}m
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-8 rounded-full overflow-hidden bg-[#F7F7F7]">
                    <div
                      className="h-full rounded-full transition-all duration-500 premium-gradient"
                      style={{
                        width: `${(day.active_users / maxActivity) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Courses by Engagement */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4 text-navy">🏆 Course-wise Highest Engagement</h3>
            {analytics?.top_courses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#4D4D4D]">No course activity yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {analytics?.top_courses.map((course, index) => (
                  <div
                    key={course.course_id}
                    className={`flex items-center justify-between p-4 rounded-xl ${index === 0 ? 'bg-blue-50' : 'bg-[#F7F7F7]'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${index === 0 ? 'premium-gradient' : 'bg-[#A5A5A5]'}`}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-bold text-navy">{course.course_title}</p>
                        <p className="text-sm text-[#4D4D4D]">Course ID: {course.course_id.substring(0, 8)}...</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-primary-blue">{course.engagement_count}</p>
                      <p className="text-xs text-[#A5A5A5]">quiz attempts</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
